import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    ArrowRightIcon,
    MapPinIcon,
} from "@heroicons/react/24/outline";

export default function PopularDestinations({ limit = 8 }) {
    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    // =========================================================
    // FETCH DESTINATIONS
    // =========================================================

    useEffect(() => {
        const fetchDestinations = async () => {
            try {
                const res = await axios.get(
                    "https://beachhoteldubai.com/api/destinations"
                );

                setDestinations(res.data);
            } catch (err) {
                console.error(
                    "Failed to load destinations:",
                    err
                );
            } finally {
                setLoading(false);
            }
        };

        fetchDestinations();
    }, []);

    // =========================================================
    // LOADING SKELETON
    // =========================================================

    if (loading) {
        return (
            <section
                className="
                    w-full
                    bg-white

                    px-4
                    py-14

                    sm:px-6
                    sm:py-16

                    lg:px-10
                    lg:py-20
                "
            >
                <div
                    className="
                        mx-auto
                        w-full
                        max-w-[1380px]
                    "
                >
                    {/* HEADER SKELETON */}

                    <div
                        className="
                            mb-10
                            flex
                            flex-col
                            gap-5

                            lg:flex-row
                            lg:items-end
                            lg:justify-between
                        "
                    >
                        <div className="space-y-4">
                            <div
                                className="
                                    h-3
                                    w-28
                                    animate-pulse
                                    rounded-full
                                    bg-[#DCE7EF]
                                "
                            />

                            <div
                                className="
                                    h-11
                                    w-[320px]
                                    max-w-full
                                    animate-pulse
                                    rounded-lg
                                    bg-[#DCE7EF]
                                "
                            />

                            <div
                                className="
                                    h-3
                                    w-[450px]
                                    max-w-full
                                    animate-pulse
                                    rounded-full
                                    bg-[#E5EDF3]
                                "
                            />
                        </div>

                        <div
                            className="
                                h-11
                                w-40
                                animate-pulse
                                rounded-full
                                bg-[#DCE7EF]
                            "
                        />
                    </div>

                    {/* CARDS SKELETON */}

                    <div
                        className="
                            grid
                            grid-cols-1
                            gap-5

                            sm:grid-cols-2

                            lg:grid-cols-4
                        "
                    >
                        {[1, 2, 3, 4].map((item) => (
                            <div
                                key={item}
                                className="
                                    h-[280px]
                                    animate-pulse
                                    overflow-hidden
                                    rounded-[24px]
                                    bg-[#DCE7EF]

                                    sm:h-[300px]

                                    lg:h-[320px]
                                "
                            />
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    // =========================================================
    // RENDER
    // =========================================================

    return (
        <section
            className="
                relative
                w-full
                overflow-hidden

                bg-white

                px-4
                py-14

                sm:px-6
                sm:py-16

                lg:px-10
                lg:py-20

                xl:py-24
            "
        >
            {/* =====================================================
                BACKGROUND DECORATION
            ===================================================== */}

            <div
                className="
                    pointer-events-none
                    absolute
                    -left-40
                    top-20

                    h-[420px]
                    w-[420px]

                    rounded-full

                    border
                    border-[#D7A74A]/15
                "
            />

            <div
                className="
                    pointer-events-none
                    absolute
                    -right-48
                    bottom-0

                    h-[500px]
                    w-[500px]

                    rounded-full

                    border
                    border-[#0B5552]/10
                "
            />

            <div
                className="
                    pointer-events-none
                    absolute
                    right-[15%]
                    top-20

                    h-2
                    w-2

                    rounded-full

                    bg-[#8B0000]
                "
            />

            <div
                className="
                    pointer-events-none
                    absolute
                    left-[12%]
                    bottom-24

                    h-2
                    w-2

                    rounded-full

                    bg-[#D7A74A]
                "
            />

            {/* =====================================================
                MAIN CONTAINER
            ===================================================== */}

            <div
                className="
                    relative
                    z-10

                    mx-auto
                    w-full
                    max-w-[1380px]
                "
            >
                {/* =================================================
                    SECTION HEADER
                ================================================= */}

                <div
                    className="
                        mb-10

                        flex
                        flex-col
                        gap-6

                        lg:mb-12
                        lg:flex-row
                        lg:items-end
                        lg:justify-between
                    "
                >
                    {/* LEFT CONTENT */}

                    <div
                        className="
                            max-w-[700px]
                        "
                    >
                        {/* EYEBROW */}

                        <div
                            className="
                                mb-4

                                flex
                                items-center
                                gap-3
                            "
                        >
                            <span
                                className="
                                    h-[2px]
                                    w-8
                                    rounded-full
                                    bg-[#8B0000]
                                "
                            />

                            <span
                                className="
                                    text-[10px]
                                    font-bold
                                    uppercase
                                    tracking-[0.22em]
                                    text-[#0B5552]
                                "
                            >
                                Explore Dubai
                            </span>
                        </div>

                        {/* HEADING */}

                        <h2
                            className="
                                font-serif
                                text-[32px]
                                font-medium
                                leading-[1.06]
                                tracking-[-0.03em]

                                text-[#0B5552]

                                sm:text-[38px]

                                md:text-[44px]

                                lg:text-[48px]
                            "
                        >
                            Discover Dubai's
                            <span
                                className="
                                    ml-2
                                    italic
                                    text-[#8B0000]
                                "
                            >
                                most-loved destinations.
                            </span>
                        </h2>

                        {/* DESCRIPTION */}

                        <p
                            className="
                                mt-4
                                max-w-[620px]

                                text-[13px]
                                leading-[1.8]
                                text-[#6B8087]

                                sm:text-[14px]
                            "
                        >
                            From iconic landmarks and vibrant
                            neighbourhoods to relaxing beachfront
                            escapes, discover the destinations
                            that make every Dubai stay memorable.
                        </p>
                    </div>

                    {/* DESKTOP VIEW ALL */}

                    <button
                        type="button"
                        onClick={() =>
                            navigate("/destinations")
                        }
                        className="
                            group

                            hidden
                            shrink-0

                            items-center
                            gap-3

                            rounded-full

                            border
                            border-[#0B5552]/20

                            bg-white

                            px-6
                            py-3.5

                            text-[11px]
                            font-bold
                            uppercase
                            tracking-[0.08em]

                            text-[#0B5552]

                            shadow-[0_8px_25px_rgba(11,85,82,0.06)]

                            transition-all
                            duration-300

                            hover:-translate-y-0.5
                            hover:border-[#D7A74A]
                            hover:bg-[#D7A74A]
                            hover:text-[#0B5552]
                            hover:shadow-[0_12px_30px_rgba(215,167,74,0.18)]

                            sm:inline-flex
                        "
                    >
                        <span>
                            Explore All Destinations
                        </span>

                        <ArrowRightIcon
                            className="
                                h-4
                                w-4

                                transition-transform
                                duration-300

                                group-hover:translate-x-1
                            "
                        />
                    </button>
                </div>

                {/* =================================================
                    DESTINATION GRID
                ================================================= */}

                <div
                    className="
                        grid
                        grid-cols-1
                        gap-5

                        sm:grid-cols-2

                        lg:grid-cols-4
                    "
                >
                    {destinations
                        .slice(0, limit)
                        .map((dest, index) => (
                            <div
                                key={dest.id}
                                onClick={() =>
                                    navigate(
                                        `/destinations/${dest.slug}`
                                    )
                                }
                                className="
                                    destination-card
                                    group

                                    relative

                                    h-[280px]

                                    cursor-pointer
                                    overflow-hidden

                                    rounded-[24px]

                                    bg-[#0B5552]

                                    shadow-[0_10px_30px_rgba(11,85,82,0.10)]

                                    transition-all
                                    duration-500

                                    hover:-translate-y-2
                                    hover:shadow-[0_22px_50px_rgba(11,85,82,0.18)]

                                    sm:h-[300px]

                                    lg:h-[320px]
                                "
                                style={{
                                    animation: `destinationFadeUp 0.55s ease-out ${index * 80
                                        }ms both`,
                                }}
                            >
                                {/* =================================================
                                    IMAGE
                                ================================================= */}

                                <img
                                    src={`https://uaediscountedhotels.com/uploads/destinations/${dest.image}`}
                                    alt={dest.name}
                                    className="
                                        absolute
                                        inset-0

                                        h-full
                                        w-full

                                        object-cover

                                        transition-transform
                                        duration-700
                                        ease-out

                                        group-hover:scale-[1.08]
                                    "
                                />

                                {/* =================================================
                                    DARK GRADIENT
                                ================================================= */}

                                <div
                                    className="
                                        pointer-events-none
                                        absolute
                                        inset-0

                                        bg-gradient-to-t
                                        from-[#042F35]/95
                                        via-[#073F3D]/35
                                        to-transparent
                                    "
                                />

                                {/* =================================================
                                    RED TOP ACCENT
                                ================================================= */}

                                <div
                                    className="
                                        absolute
                                        left-0
                                        right-0
                                        top-0

                                        h-[4px]

                                        bg-gradient-to-r
                                        from-[#8B0000]
                                        via-[#D7A74A]
                                        to-transparent

                                        opacity-80
                                    "
                                />

                                {/* =================================================
                                    HOTEL COUNT
                                ================================================= */}

                                <div
                                    className="
                                        absolute
                                        right-4
                                        top-4
                                    "
                                >
                                    <span
                                        className="
                                            inline-flex
                                            items-center

                                            rounded-full

                                            border
                                            border-white/25

                                            bg-[#0B5552]/65

                                            px-3
                                            py-1.5

                                            text-[9px]
                                            font-semibold

                                            text-white

                                            shadow-lg

                                            backdrop-blur-md
                                        "
                                    >
                                        {dest.hotels_count || 0} Hotels
                                    </span>
                                </div>

                                {/* =================================================
                                    LOCATION PILL
                                ================================================= */}

                                <div
                                    className="
                                        absolute
                                        left-4
                                        top-4
                                    "
                                >
                                    <div
                                        className="
                                            flex
                                            items-center
                                            gap-1.5

                                            rounded-full

                                            bg-white/95

                                            px-3
                                            py-1.5

                                            shadow-lg

                                            backdrop-blur-sm
                                        "
                                    >
                                        <MapPinIcon
                                            className="
                                                h-3
                                                w-3
                                                text-[#8B0000]
                                            "
                                            strokeWidth={2.5}
                                        />

                                        <span
                                            className="
                                                text-[9px]
                                                font-bold
                                                uppercase
                                                tracking-[0.1em]
                                                text-[#0B5552]
                                            "
                                        >
                                            {dest.name}
                                        </span>
                                    </div>
                                </div>

                                {/* =================================================
                                    BOTTOM CONTENT
                                ================================================= */}

                                <div
                                    className="
                                        absolute
                                        bottom-0
                                        left-0
                                        right-0

                                        p-5
                                    "
                                >
                                    {/* SMALL LABEL */}

                                    <div
                                        className="
                                            mb-2

                                            flex
                                            items-center
                                            gap-2
                                        "
                                    >
                                        <span
                                            className="
                                                h-[2px]
                                                w-5
                                                bg-[#D7A74A]
                                            "
                                        />

                                        <span
                                            className="
                                                text-[9px]
                                                font-semibold
                                                uppercase
                                                tracking-[0.15em]
                                                text-white/70
                                            "
                                        >
                                            Destination
                                        </span>
                                    </div>

                                    {/* NAME + ARROW */}

                                    <div
                                        className="
                                            flex
                                            items-end
                                            justify-between
                                            gap-4
                                        "
                                    >
                                        <div className="min-w-0">
                                            <h3
                                                className="
                                                    truncate

                                                    font-serif
                                                    text-[22px]
                                                    font-medium
                                                    leading-tight

                                                    text-white

                                                    sm:text-[24px]
                                                "
                                            >
                                                {dest.name}
                                            </h3>

                                            <p
                                                className="
                                                    mt-1.5

                                                    text-[10px]
                                                    font-medium

                                                    text-white/65
                                                "
                                            >
                                                {dest.hotels_count || 0}
                                                {" "}
                                                {Number(
                                                    dest.hotels_count
                                                ) === 1
                                                    ? "hotel"
                                                    : "hotels"}{" "}
                                                available
                                            </p>
                                        </div>

                                        {/* ARROW */}

                                        <div
                                            className="
                                                flex
                                                h-11
                                                w-11
                                                shrink-0

                                                items-center
                                                justify-center

                                                rounded-full

                                                border
                                                border-white/25

                                                bg-[#D7A74A]

                                                text-white

                                                backdrop-blur-md

                                                transition-all
                                                duration-300

                                                group-hover:translate-x-1
                                                group-hover:border-[#8B0000]
                                                group-hover:bg-[#8B0000]
                                                group-hover:text-[white]
                                            "
                                        >
                                            <ArrowRightIcon
                                                className="
                                                    h-4
                                                    w-4
                                                "
                                                strokeWidth={2}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* =================================================
                                    BOTTOM GOLD LINE
                                ================================================= */}

                                <div
                                    className="
                                        absolute
                                        bottom-0
                                        left-0

                                        h-[3px]
                                        w-0

                                        bg-[#D7A74A]

                                        transition-all
                                        duration-500

                                        group-hover:w-full
                                    "
                                />
                            </div>
                        ))}
                </div>

                {/* =================================================
                    MOBILE VIEW ALL
                ================================================= */}

                <div className="mt-7 sm:hidden">
                    <button
                        type="button"
                        onClick={() =>
                            navigate("/destinations")
                        }
                        className="
                            group

                            flex
                            w-full

                            items-center
                            justify-center
                            gap-2.5

                            rounded-full

                            border
                            border-[#0B5552]

                            bg-white

                            px-5
                            py-3.5

                            text-[11px]
                            font-bold
                            uppercase
                            tracking-[0.08em]

                            text-[#0B5552]

                            shadow-[0_6px_20px_rgba(11,85,82,0.06)]

                            transition-all
                            duration-300

                            hover:bg-[#0B5552]
                            hover:text-white
                        "
                    >
                        <span>
                            View All Destinations
                        </span>

                        <ArrowRightIcon
                            className="
                                h-4
                                w-4

                                transition-transform
                                duration-300

                                group-hover:translate-x-1
                            "
                        />
                    </button>
                </div>

                {/* =================================================
                    TRUST LINE
                ================================================= */}

                <div
                    className="
                        mt-10

                        flex
                        flex-wrap
                        items-center
                        justify-center

                        gap-x-5
                        gap-y-3

                        border-t
                        border-[#E1E9EF]

                        pt-7
                    "
                >
                    <span
                        className="
                            text-[9px]
                            font-bold
                            uppercase
                            tracking-[0.14em]
                            text-[#7B8D93]
                        "
                    >
                        Iconic locations
                    </span>

                    <span
                        className="
                            h-1
                            w-1
                            rounded-full
                            bg-[#D7A74A]
                        "
                    />

                    <span
                        className="
                            text-[9px]
                            font-bold
                            uppercase
                            tracking-[0.14em]
                            text-[#7B8D93]
                        "
                    >
                        Beachfront escapes
                    </span>

                    <span
                        className="
                            h-1
                            w-1
                            rounded-full
                            bg-[#D7A74A]
                        "
                    />

                    <span
                        className="
                            text-[9px]
                            font-bold
                            uppercase
                            tracking-[0.14em]
                            text-[#7B8D93]
                        "
                    >
                        Luxury stays
                    </span>
                </div>
            </div>

            {/* =====================================================
                CARD ANIMATION
            ===================================================== */}

            <style>
                {`
                    @keyframes destinationFadeUp {
                        from {
                            opacity: 0;
                            transform: translateY(18px);
                        }

                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @media (prefers-reduced-motion: reduce) {
                        .destination-card {
                            animation: none !important;
                            transition: none !important;
                        }
                    }
                `}
            </style>
        </section>
    );
}